import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { pickLocale } from "@/lib/localize";
import { TranslationFallbackNotice } from "@/components/TranslationFallbackNotice";
import { getCategory } from "@/lib/libraryCategories";
import { fetchLibraryEntry, fetchLibraryEntryBySlug, fetchCategoryEntries } from "@/lib/libraryApi";
import { VIRTUE_JOURNEYS } from "@/lib/journeyNetwork";
import { LibraryBreadcrumb, breadcrumbJsonLd } from "@/components/LibraryBreadcrumb";
import MarkdownRenderer from "@/components/journal/MarkdownRenderer";

/**
 * One entry of the Living Library — the Phase 2 blueprint page.
 * Renders title, summary, markdown body and every populated "related"
 * connection (Academy lessons, Journal articles, videos, sources,
 * research, concepts, virtues, philosophers, exercises, books) without
 * ever needing a redesign. Empty connection types simply don't render.
 */

// How each related-content type links out. Library-internal types link to
// their collection; journal slugs link to the article; the rest render as
// quiet references until their destinations exist.
const RELATED_LINKS = {
  journal_articles: (v) => `/stoic-journal/${v}`,
  original_sources: (v) => `/library/sources/${v}`,
  modern_research: (v) => `/library/research/${v}`,
  related_concepts: (v) => `/library/concepts/${v}`,
  related_virtues: (v) => `/library/${v}`,
  related_philosophers: (v) => `/library/philosophers/${v}`,
};

const RELATED_ORDER = [
  "academy_lessons", "virtue_guide_conversations", "journal_articles",
  "videos", "original_sources", "modern_research", "related_concepts",
  "related_virtues", "related_philosophers", "exercises", "books",
];

export default function LibraryEntry({ flatSlug = "" }) {
  const params = useParams();
  const categoryId = flatSlug ? "" : params.categoryId;
  const slug = flatSlug || params.slug;
  const { t, lang } = useT();
  const [entry, setEntry] = useState(null);   // null = loading
  const [missing, setMissing] = useState(false);
  const [publishedVirtues, setPublishedVirtues] = useState([]);
  // Canonical rule: one entity, one permanent URL — /library/{slug} is the
  // canonical address; the legacy /library/{category}/{slug} route keeps
  // working and declares the flat URL as canonical.
  const category = getCategory(flatSlug ? entry?.category : categoryId);
  // Founder preview: with the admin token present (set once by logging in
  // at /admin), REVIEW entries open automatically — direct URLs included.
  // Public visitors have no token and only ever see published entries.

  useEffect(() => {
    if (!flatSlug && !getCategory(categoryId)) return;
    let alive = true;
    setEntry(null);
    setMissing(false);
    let adminToken = "";
    try { adminToken = localStorage.getItem("stoicos.admin.token") || ""; } catch { /* ignore */ }
    const publicFetch = () => flatSlug
      ? fetchLibraryEntryBySlug(slug, {})
      : fetchLibraryEntry(categoryId, slug, {});
    const load = adminToken
      ? (flatSlug
          ? fetchLibraryEntryBySlug(slug, { preview: true, token: adminToken }).catch(publicFetch)
          : fetchLibraryEntry(categoryId, slug, { preview: true, token: adminToken }).catch(publicFetch))
      : publicFetch();
    load
      .then((data) => { if (alive) setEntry(data); })
      .catch(() => { if (alive) setMissing(true); });
    // The Journey Network updates itself: cards become links the moment
    // a Virtue Journey is published — no manual editing ever.
    fetchCategoryEntries("virtues")
      .then((data) => { if (alive) setPublishedVirtues((data.entries || []).map((e) => e.slug)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [categoryId, slug, flatSlug]);

  const title = entry ? pickLocale(entry.title, lang) : "";
  const canonicalUrl = `https://stoicos.ai/library/${slug}`;
  const crumbs = [
    { label: t("academy.nav"), to: "/academy", href: "https://stoicos.ai/academy" },
    { label: t("library.hero.title"), to: "/library", href: "https://stoicos.ai/library" },
    ...(category ? [{ label: t(`library.cats.${category.id}.title`), to: `/library/${category.id}`, href: `https://stoicos.ai/library/${category.id}` }] : []),
    ...(title ? [{ label: title, href: canonicalUrl }] : []),
  ];
  const heroAbs = entry?.hero ? (entry.hero.startsWith("http") ? entry.hero : `https://stoicos.ai${entry.hero}`) : "";

  useSEO({
    title: title ? `${title} | ${t("library.hero.title")} | StoicOs.ai` : "StoicOs.ai",
    description: entry ? pickLocale(entry.summary, lang) : "",
    canonical: canonicalUrl,
    og: entry ? {
      title: `${title} | ${t("library.hero.title")}`,
      description: pickLocale(entry.summary, lang),
      image: heroAbs || undefined,
      imageType: /\.jpe?g($|\?)/i.test(heroAbs) ? "image/jpeg" : /\.png($|\?)/i.test(heroAbs) ? "image/png" : undefined,
      type: "article",
    } : undefined,
    jsonLd: entry ? [
      breadcrumbJsonLd(crumbs),
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: title,
        description: pickLocale(entry.summary, lang),
        ...(heroAbs ? { image: heroAbs } : {}),
        mainEntityOfPage: canonicalUrl,
        author: { "@type": "Organization", name: "StoicOs.ai" },
        publisher: { "@type": "Organization", name: "StoicOs.ai", url: "https://stoicos.ai" },
      },
    ] : undefined,
  });

  if (!flatSlug && !getCategory(categoryId)) return <Navigate to="/library" replace />;
  if (missing) return <Navigate to={category ? `/library/${category.id}` : "/library"} replace />;

  return (
    <main data-testid="library-entry-page" className="bg-ink text-bone min-h-screen page-fade-in">
      <section className="relative pt-40 md:pt-52 pb-28 md:pb-40 px-6 sm:px-10">
        <div className="max-w-[41rem] mx-auto mb-14 md:mb-20">
          <LibraryBreadcrumb items={crumbs} testid="library-entry-breadcrumb" />
        </div>

        {/* Journey Hero — the Guardian Symbol. The artwork is used exactly
            as provided: desktop/tablet show the complete panorama at its
            natural ratio (no crop — the eyes and beak always remain), mobile
            uses a centred panoramic crop that keeps both eyes and the beak.
            Movement: only the near-imperceptible 40s Ken Burns. Text never
            enters the artwork. */}
        {entry?.hero && (
          <figure
            data-testid="library-entry-hero"
            className={entry.hero_full
              ? "relative w-full max-w-[88rem] mx-auto mb-14 md:mb-20 overflow-hidden bg-black"
              : "relative w-screen left-1/2 -translate-x-1/2 mb-8 md:mb-10 overflow-hidden bg-black"}
          >
            <div className="library-hero-kenburns">
              <picture>
                <source media="(min-width: 1024px)" srcSet={entry.hero_desktop || entry.hero} />
                <img
                  src={entry.hero}
                  alt={title}
                  data-testid="library-entry-hero-image"
                  className={entry.hero_full
                    ? "w-full h-[340px] md:h-[480px] lg:h-[580px] object-cover object-center"
                    : "w-full h-[32svh] min-h-[210px] object-cover object-center lg:h-[56svh] lg:min-h-[380px]"}
                />
              </picture>
            </div>
          </figure>
        )}

        <div className="max-w-[41rem] mx-auto">
          {entry === null ? (
            <p className="text-sm text-fog italic font-serif">…</p>
          ) : (
            <>
              <TranslationFallbackNotice fields={[entry.title, entry.summary, entry.body]} lang={lang} />
              <div className="mb-3 flex items-center gap-3">
                {(entry.tags || []).filter((tg) => /^journey/i.test(tg)).slice(0, 1).map((tg) => (
                  <span key={tg} data-testid="library-entry-journey-label"
                    className="text-[10px] tracking-[0.4em] uppercase text-gold">
                    {tg}
                  </span>
                ))}
              </div>
              <h1 data-testid="library-entry-title" className="font-serif text-5xl sm:text-6xl text-bone leading-[1.08] tracking-tight">
                {pickLocale(entry.title_initial, lang) ? (
                  <img
                    src={pickLocale(entry.title_initial, lang)}
                    alt=""
                    aria-hidden
                    data-testid="library-entry-title-initial"
                    className="journey-title-initial-img"
                  />
                ) : (
                  <span className="journey-title-initial" aria-hidden>{title.charAt(0)}</span>
                )}
                <span className="sr-only">{title.charAt(0)}</span>
                {title.slice(1)}
              </h1>
              {pickLocale(entry.summary, lang) && (
                <p className="mt-8 md:mt-10 font-serif italic text-lg md:text-xl text-gold/90 leading-relaxed">
                  {pickLocale(entry.summary, lang)}
                </p>
              )}
              <div className="gold-rule w-24 my-14 md:my-16" aria-hidden />

              {pickLocale(entry.body, lang) && (
                <div data-testid="library-entry-body" className="journey-illuminated">
                  <MarkdownRenderer source={pickLocale(entry.body, lang)} />
                </div>
              )}

              {/* =====================================================
                  CONTINUE YOUR JOURNEY — the Journey Network.
                  The remaining Cardinal Virtues, presented as an
                  invitation, never as website navigation. Cards open
                  automatically once their Journey is published.
                  ===================================================== */}
              <div data-testid="library-entry-continue" className="mt-16 border-t border-white/10 pt-12">
                <h2 className="text-[11px] tracking-[0.4em] uppercase text-gold mb-8">
                  {t("library.entry.relatedTitle")}
                </h2>

                {category.id === "virtues" && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5" data-testid="journey-network">
                    {VIRTUE_JOURNEYS.filter((j) => j.slug !== slug).map((j) => {
                      const isOpen = publishedVirtues.includes(j.slug);
                      const inner = (
                        <>
                          <div className="flex items-center justify-between gap-3">
                            {j.guardianImg ? (
                              <img src={j.guardianImg} alt="" aria-hidden
                                className="h-10 w-10 object-cover object-center opacity-90" />
                            ) : (
                              <j.Icon size={22} strokeWidth={1.25} className="text-gold/80" aria-hidden />
                            )}
                            <span className="text-[9px] tracking-[0.3em] uppercase text-bone/40">
                              {t("library.journey.label")} {j.number}
                            </span>
                          </div>
                          <h3 className="mt-5 font-serif text-xl text-bone">
                            {t(`library.journey.${j.slug}.name`)}
                          </h3>
                          <p className="mt-2 text-sm text-fog font-reading leading-relaxed flex-1">
                            {t(`library.journey.${j.slug}.invite`)}
                          </p>
                          {!isOpen && (
                            <p className="mt-4 text-[10px] tracking-[0.24em] uppercase text-gold/60 italic">
                              {t("library.journey.soon")}
                            </p>
                          )}
                        </>
                      );
                      const cardClass = "flex flex-col h-full border p-6 md:p-7";
                      return isOpen ? (
                        <Link
                          key={j.slug}
                          to={`/library/${j.slug}`}
                          data-testid={`journey-card-${j.slug}`}
                          className={`${cardClass} border-white/10 hover:border-gold/50 bg-white/[0.015] hover:bg-gold/[0.03]
                            transition-[transform,box-shadow,border-color,background-color] duration-300
                            hover:-translate-y-1 hover:shadow-[0_18px_50px_-24px_rgba(232,200,121,0.35)]`}
                        >
                          {inner}
                        </Link>
                      ) : (
                        <div key={j.slug} data-testid={`journey-card-${j.slug}`}
                          className={`${cardClass} border-white/10 bg-white/[0.01]`}>
                          {inner}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Connected knowledge chips — render only when populated */}
                {RELATED_ORDER.some((k) => (entry.related?.[k] || []).length > 0) && (
                  <div data-testid="library-entry-related" className="mt-10 flex flex-col gap-8">
                    {RELATED_ORDER.map((k) => {
                      const values = entry.related?.[k] || [];
                      if (values.length === 0) return null;
                      const linkFor = RELATED_LINKS[k];
                      return (
                        <div key={k} data-testid={`library-entry-related-${k}`}>
                          <p className="text-[10px] tracking-[0.28em] uppercase text-bone/50 mb-3">
                            {t(`library.rel.${k}`)}
                          </p>
                          <ul className="flex flex-wrap gap-2.5">
                            {values.map((v) => (
                              <li key={v}>
                                {linkFor ? (
                                  <Link
                                    to={linkFor(v)}
                                    className="inline-flex items-center px-4 py-2 border border-white/12 hover:border-gold/50 text-bone/85 hover:text-gold text-[11px] tracking-[0.2em] uppercase transition-colors"
                                  >
                                    {v}
                                  </Link>
                                ) : (
                                  <span className="inline-flex items-center px-4 py-2 border border-white/12 text-bone/70 text-[11px] tracking-[0.2em] uppercase">
                                    {v}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="mt-12">
                  <Link
                    to="/library"
                    data-testid="library-entry-return"
                    className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-bone/60 hover:text-gold transition-colors"
                  >
                    <ArrowLeft size={13} strokeWidth={1.75} />
                    {t("library.journey.return")}
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
